import React, { useState, useEffect, useMemo, useRef } from "react";
import { Box, Divider } from "@mui/material";
import { v4 as uuidv4 } from "uuid";
import ScheduleHeader from "../../App/ScheduleHeader";
import ScheduleContainer from "../utils/ScheduleContainer";
import ScheduleNotes from "../ScheduleNotes";
import { getCommaOut, formatAmount } from "../../../utils/hooks/dataHandler";
import { useErrorDialog } from "../../../contexts/ErrorDialogContext";
import {
  calculateTrackingSchedule,
  calculateTotalsByIncomeTypeAndCountry,
  calculateTrackingRow,
  DEFAULT_COUNTRY,
  getCountry,
  sortByYaEarned,
} from "./calculations";
import PdfGenerator from "./Pdf/UnremittedForeignPdf";
import ForeignIncomeTrackerExcelUpload from "./ExcelUpload";
import ForeignIncomeTrackerTabs from "./ForeignIncomeTrackerTabs";
import ForeignIncomeGuideline from "./Guidelines";

const ForeignIncomeTrackerSchedule = ({
  company,
  schedule,
  scheduleDetails,
  saveSchedule,
  deleteSchedule,
  canEditSchedule = true,
}) => {
  const [schName, setSchName] = useState(`Foreign Income Tracker`);
  const [notes, setNotes] = useState("");
  const [reviewNotes, setReviewNotes] = useState("");
  const [guideOpen, setGuideOpen] = useState(false);

  const [currentYearData, setCurrentYearData] = useState([]);
  const [previousYearData, setPreviousYearData] = useState([]);
  const [trackingScheduleData, setTrackingScheduleData] = useState([]);
  const [previousYearSortDirection, setPreviousYearSortDirection] = useState(null);
  const [trackingSortDirection, setTrackingSortDirection] = useState(null);
  // FTC claims grouped by income type and country: { [incomeType]: { [country]: boolean } }
  const [ftcClaims, setFtcClaims] = useState({});
  const [foreignTaxCreditScheduleRef, setForeignTaxCreditScheduleRef] =
    useState("");

  // Ref to track if initial sync has been performed
  const hasInitialSyncRun = useRef(false);

  // Excel upload dialog state
  const [excelDialogOpen, setExcelDialogOpen] = useState(false);
  const handleOpenExcelUploadDialog = () => {
    setExcelDialogOpen(true);
  };
  const handleCloseExcelUploadDialog = () => setExcelDialogOpen(false);

  const onExcelAddRows = (updatedData) => {
    let updatedPreviousYearData = [...previousYearData];
    updatedData.forEach((row) => {
      const newRow = {
        rowId: uuidv4(),
        description: "",
        incomeType: "",
        country: "",
        yaEarned: "",
        amount: 0,
        allowableExpenses: 0,
        foreignTaxSuffered: 0,
        reference: "",
        isLocked: false,
        isPriorYear: true,
      };

      Object.keys(row).forEach((key) => {
        newRow[key] = row[key];
      });

      updatedPreviousYearData.push(newRow);
    });

    setPreviousYearData(updatedPreviousYearData);

    handleCloseExcelUploadDialog();
  };

  const handleError = useErrorDialog();

  // Generic helper: Update a field in a row
  const updateRowField = (data, setData, rowId, field, value, fieldType) => {
    if (data.length === 0) return;
    const updatedRows = [...data];
    const rowIndex = updatedRows.findIndex((row) => row.rowId === rowId);
    if (rowIndex === -1) return;

    const processedValue =
      fieldType === "numeric" ? Number(getCommaOut(value)) : value;
    updatedRows[rowIndex][field] = processedValue;
    setData(updatedRows);
  };

  // Generic helper: Unlock a row and remove from tracking schedule
  const unlockRow = (data, setData, rowId) => {
    const updatedData = [...data];
    const rowIndex = updatedData.findIndex((row) => row.rowId === rowId);
    if (rowIndex === -1) return;

    const trackingRow = trackingScheduleData.find(
      (item) => item.sourceId === rowId
    );
    updatedData[rowIndex] = { ...updatedData[rowIndex], isLocked: false };
    if (trackingRow) {
      // Keep tracking inputs on the source row so they survive saving while unlocked.
      updatedData[rowIndex].trackingInputs = {
        unremittedIncomeBf: trackingRow.unremittedIncomeBf,
        incomeReceived: trackingRow.incomeReceived,
        incomeUsedNotReceived: trackingRow.incomeUsedNotReceived,
        allowableExpensesBf: trackingRow.allowableExpensesBf,
      };
    }
    setData(updatedData);

    // Remove from tracking schedule
    let updatedTracking = trackingScheduleData.filter(
      (item) => item.sourceId !== rowId
    );

    // Claims belong to a group; preserve them while other rows remain in that group.
    if (
      trackingRow &&
      !updatedTracking.some(
        (item) =>
          item.incomeType === trackingRow.incomeType &&
          getCountry(item.country) === getCountry(trackingRow.country)
      )
    ) {
      setFtcClaims((prev) => ({
        ...prev,
        [trackingRow.incomeType]: {
          ...prev[trackingRow.incomeType],
          [getCountry(trackingRow.country)]: false,
        },
      }));
    }

    if (updatedTracking && updatedTracking.length > 0) {
      updatedTracking = calculateTrackingSchedule(updatedTracking);
    }
    setTrackingScheduleData(updatedTracking);
  };

  // Generic helper: Delete a row
  const deleteRow = (data, setData, rowId) => {
    const rowIndex = data.findIndex((row) => row.rowId === rowId);
    if (rowIndex === -1) return;
    const row = data[rowIndex];

    // If the row is locked, also remove it from tracking schedule
    if (row.isLocked) {
      const updatedTracking = trackingScheduleData.filter(
        (item) => item.sourceId !== rowId
      );
      setTrackingScheduleData(updatedTracking);
    }

    // Remove from data
    const updatedData = data.filter((row) => row.rowId !== rowId);
    setData(updatedData);
  };

  // Generic helper: Calculate totals for income data
  const calculateIncomeTotals = (data) => {
    return data?.reduce(
      (acc, row) => {
        acc.amount += row.amount || 0;
        acc.allowableExpenses += row.allowableExpenses || 0;
        acc.foreignTaxSuffered += row.foreignTaxSuffered || 0;
        return acc;
      },
      { amount: 0, allowableExpenses: 0, foreignTaxSuffered: 0 }
    );
  };

  useEffect(() => {
    if (!schedule || !scheduleDetails) return;
    //init
    if (schedule) {
      setSchName(schedule.name);
      setNotes(schedule.notes);
      setReviewNotes(schedule.reviewNotes);
    }

    if (scheduleDetails) {
      let dataRows = scheduleDetails.dataRows?.rows;
      let previousYearRows = scheduleDetails.dataRows?.previousYearData;
      let trackingRows = scheduleDetails.dataRows?.trackingScheduleData;
      let ref = scheduleDetails.foreignTaxCreditScheduleRef;

      if (trackingRows && trackingRows.length > 0) {
        trackingRows = calculateTrackingSchedule(trackingRows);
      }
      setCurrentYearData(dataRows || []);
      setPreviousYearData(previousYearRows || []);
      setTrackingScheduleData(trackingRows || []);
      setPreviousYearSortDirection(null);
      setTrackingSortDirection(null);
      setForeignTaxCreditScheduleRef(ref);
      // Load FTC claims if saved
      // Preserve claims saved under the legacy blank-country group.
      setFtcClaims(
        Object.fromEntries(
          Object.entries(scheduleDetails.dataRows?.ftcClaims || {}).map(
            ([incomeType, claims]) => {
              const { "": blankCountryClaim, ...countryClaims } = claims;
              if (blankCountryClaim !== undefined) {
                countryClaims[DEFAULT_COUNTRY] =
                  countryClaims[DEFAULT_COUNTRY] ?? blankCountryClaim;
              }
              return [incomeType, countryClaims];
            }
          )
        )
      );
      // Reset sync flag when new data is loaded
      hasInitialSyncRun.current = false;
    }
  }, [schedule, scheduleDetails]);

  // Sync tracking schedule data with source data on initial load only
  // This ensures tracking rows are synced with current/previous year data when first loaded
  useEffect(() => {
    // Only run once on initial load
    if (hasInitialSyncRun.current) return;

    if (
      trackingScheduleData.length === 0 ||
      (!currentYearData?.length && !previousYearData?.length)
    )
      return;

    const updatedTracking = trackingScheduleData.map((trackingRow) => {
      // Find matching current year row by sourceId
      const currentYearRow = currentYearData?.find(
        (row) => row.rowId === trackingRow.sourceId && row.isLocked
      );

      // Find matching previous year row by sourceId
      const previousYearRow = previousYearData?.find(
        (row) => row.rowId === trackingRow.sourceId && row.isLocked
      );

      if (currentYearRow) {
        // Update from current year data
        return {
          ...trackingRow,
          currentYearIncome: currentYearRow.amount || 0,
          currentYearAllowableExpenses: currentYearRow.allowableExpenses || 0,
          foreignTaxSufferedCf: currentYearRow.foreignTaxSuffered || 0,
          yaEarned: currentYearRow.yaEarned || company.yearOfAssessment,
          // Update income type and country if changed
          incomeType: currentYearRow.incomeType || trackingRow.incomeType,
          country: getCountry(currentYearRow.country),
        };
      } else if (previousYearRow) {
        // Update from previous year data
        const foreignTaxSuffered = previousYearRow.foreignTaxSuffered || 0;
        return {
          ...trackingRow,
          currentYearIncome: previousYearRow.amount || 0,
          currentYearAllowableExpenses: previousYearRow.allowableExpenses || 0,
          foreignTaxSufferedBf: foreignTaxSuffered,
          foreignTaxSufferedCf: foreignTaxSuffered,
          yaEarned: previousYearRow.yaEarned || trackingRow.yaEarned,
          // Update income type and country if changed
          incomeType: previousYearRow.incomeType || trackingRow.incomeType,
          country: getCountry(previousYearRow.country),
        };
      }

      // No matching source row, return unchanged
      return trackingRow;
    });

    // Apply validation to ensure totalUsedIncome doesn't exceed totalAvailableIncome
    // This is needed because currentYearIncome may have been updated during sync
    const validatedTracking = updatedTracking.map((row) => {
      const unremittedIncomeBf = row.unremittedIncomeBf || 0;
      const incomeReceived = row.incomeReceived || 0;
      const incomeUsedNotReceived = row.incomeUsedNotReceived || 0;
      const currentYearIncome = row.currentYearIncome || 0;
      const totalAvailableIncome = unremittedIncomeBf + currentYearIncome;
      const totalUsedIncome = incomeReceived + incomeUsedNotReceived;

      if (totalUsedIncome > totalAvailableIncome) {
        return {
          ...row,
          incomeReceived: totalAvailableIncome,
          incomeUsedNotReceived: 0,
        };
      }

      row = calculateTrackingRow(row);

      return row;
    });

    // Only update if there are actual changes to avoid infinite loops
    const hasChanges = validatedTracking.some((updatedRow, index) => {
      const originalRow = trackingScheduleData[index];
      return (
        updatedRow.currentYearIncome !== originalRow.currentYearIncome ||
        updatedRow.currentYearAllowableExpenses !==
          originalRow.currentYearAllowableExpenses ||
        updatedRow.foreignTaxSufferedBf !== originalRow.foreignTaxSufferedBf ||
        updatedRow.foreignTaxSufferedCf !== originalRow.foreignTaxSufferedCf ||
        updatedRow.yaEarned !== originalRow.yaEarned ||
        updatedRow.incomeType !== originalRow.incomeType ||
        updatedRow.country !== originalRow.country ||
        updatedRow.incomeReceived !== originalRow.incomeReceived ||
        updatedRow.incomeUsedNotReceived !== originalRow.incomeUsedNotReceived
      );
    });

    if (hasChanges) {
      setTrackingScheduleData(validatedTracking);
      hasInitialSyncRun.current = true; // Mark as completed
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentYearData, previousYearData, trackingScheduleData]);

  const handleSave = () => {
    schedule.name = schName;
    schedule.notes = notes;
    schedule.reviewNotes = reviewNotes;
    saveSchedule(schedule, {
      dataRows: currentYearData,
      previousYearData: previousYearData,
      trackingScheduleData: trackingScheduleData,
      calculatedTrackingData: trackingSubtotals,
      ftcClaims: ftcClaims,
      doc: JSON.stringify({
        taxAdjustment: schedule?.taxAdjustment || "",
        currentYearUnremittedForeignRows: currentYearData,
        previousYearUnremittedForeignRows: previousYearData,
        foreignSourceTrackingRows: trackingScheduleData,
        trackingSubtotals: flatTotals,
        summaryData: trackingSubtotals,
        foreignTaxCreditScheduleRef,
        notes,
        reviewNotes,
      }),
    });
  };

  const createTrackingRowFromSource = (row, isPriorYear = false) => {
    const foreignTaxSuffered = row.foreignTaxSuffered || 0;
    const trackingRow = {
      rowId: uuidv4(),
      incomeType: row.incomeType,
      country: getCountry(row.country),
      yaEarned: isPriorYear
        ? row.yaEarned
        : row.yaEarned || company.yearOfAssessment,
      unremittedIncomeBf: row.trackingInputs?.unremittedIncomeBf || 0,
      currentYearIncome: row.amount || 0,
      incomeReceived: row.trackingInputs?.incomeReceived || 0,
      incomeUsedNotReceived: row.trackingInputs?.incomeUsedNotReceived || 0,
      allowableExpensesBf: row.trackingInputs?.allowableExpensesBf || 0,
      currentYearAllowableExpenses: row.allowableExpenses || 0,
      shareOfExpensesRemitted: 0,
      shareOfExpensesNonRemitted: 0,
      allowableExpensesCf: 0,
      netIncomeReceived: 0,
      foreignTaxSufferedBf: isPriorYear ? foreignTaxSuffered : 0,
      foreignTaxSuffered: isPriorYear ? 0 : foreignTaxSuffered,
      foreignTaxSufferedCf: isPriorYear
        ? foreignTaxSuffered
        : foreignTaxSuffered, // Current year foreign tax goes to Cf
      shareOfForeignTaxSufferedRemitted: 0,
      shareOfForeignTaxSufferedNonRemitted: 0,
      ftcClaim: false,
      sourceId: row.rowId,
      isPriorYear: isPriorYear,
    };
    return calculateTrackingRow(trackingRow);
  };

  // Handle saving a row from current year to tracking schedule
  const handleLockRow = (rowId) => {
    const rowIndex = currentYearData.findIndex((row) => row.rowId === rowId);
    const row = currentYearData[rowIndex];
    if (!row.incomeType) {
      handleError("Please fill in all required fields (Income Type)");
      return;
    }

    // Mark row as saved
    const updatedCurrentYear = [...currentYearData];
    updatedCurrentYear[rowIndex] = { ...row, isLocked: true };
    setCurrentYearData(updatedCurrentYear);

    // Add to tracking schedule
    const newTrackingRow = createTrackingRowFromSource(row, false);
    setTrackingScheduleData([...trackingScheduleData, newTrackingRow]);
  };

  // Handle saving a row from previous year to tracking schedule
  const handleLockPreviousYearRow = (rowId) => {
    const rowIndex = previousYearData.findIndex((row) => row.rowId === rowId);
    const row = previousYearData[rowIndex];
    if (!row.incomeType || !row.yaEarned) {
      handleError(
        "Please fill in all required fields (Income Type, YA Earned)"
      );
      return;
    }

    // Mark row as saved
    const updatedPreviousYear = [...previousYearData];
    updatedPreviousYear[rowIndex] = { ...row, isLocked: true };
    setPreviousYearData(updatedPreviousYear);

    // Add to tracking schedule
    const newTrackingRow = createTrackingRowFromSource(row, true);
    setTrackingScheduleData([...trackingScheduleData, newTrackingRow]);
  };

  // Handle unsaving a row (wrapper for current year)
  const handleUnlockRow = (rowId) => {
    unlockRow(currentYearData, setCurrentYearData, rowId);
  };

  // Handle unsaving a previous year row (wrapper)
  const handleUnlockPreviousYearRow = (rowId) => {
    unlockRow(previousYearData, setPreviousYearData, rowId);
  };

  // Handle current year income change (wrapper)
  const handleCurrentYearIncomeChange = (
    value,
    { rowId, field, fieldType }
  ) => {
    updateRowField(
      currentYearData,
      setCurrentYearData,
      rowId,
      field,
      value,
      fieldType
    );
  };

  // Handle previous year income change (wrapper)
  const handlePreviousYearIncomeChange = (
    value,
    { rowId, field, fieldType }
  ) => {
    updateRowField(
      previousYearData,
      setPreviousYearData,
      rowId,
      field,
      value,
      fieldType
    );
  };

  // Handle deleting a previous year row (wrapper)
  const handleDeletePreviousYearRow = (rowId) => {
    deleteRow(previousYearData, setPreviousYearData, rowId);
  };

  const handleSortTrackingRows = (direction) => {
    setTrackingScheduleData((rows) => sortByYaEarned(rows, direction));
    setTrackingSortDirection(direction);
  };

  const handleSortPreviousYearRows = (direction) => {
    setPreviousYearData((rows) => sortByYaEarned(rows, direction));
    setPreviousYearSortDirection(direction);
    // The tracker and PDF use the same ordered rows, including locked prior years.
    handleSortTrackingRows(direction);
  };

  // Handle adding a new previous year row
  const addPreviousYearRow = () => {
    const newRow = {
      rowId: uuidv4(),
      description: "",
      incomeType: "",
      country: "",
      yaEarned: "",
      amount: 0,
      allowableExpenses: 0,
      foreignTaxSuffered: 0,
      reference: "",
      isLocked: false,
      isPriorYear: true,
    };
    setPreviousYearData([...(previousYearData || []), newRow]);
  };

  // Handle tracking schedule change
  const handleTrackingScheduleChange = (value, { rowId, field, fieldType }) => {
    if (trackingScheduleData.length === 0) return;

    const updatedRows = [...trackingScheduleData];
    const rowIndex = updatedRows.findIndex((row) => row.rowId === rowId);
    if (rowIndex === -1) return;

    const row = updatedRows[rowIndex];
    let processedValue =
      fieldType === "numeric" ? Number(getCommaOut(value)) : value;

    if (
      field === "incomeReceived" ||
      field === "unremittedIncomeBf" ||
      field === "incomeUsedNotReceived"
    ) {
      const unremittedIncomeBf =
        field === "unremittedIncomeBf"
          ? processedValue
          : row.unremittedIncomeBf || 0;
      const incomeReceived =
        field === "incomeReceived" ? processedValue : row.incomeReceived || 0;
      const incomeUsedNotReceived =
        field === "incomeUsedNotReceived"
          ? processedValue
          : row.incomeUsedNotReceived || 0;
      const currentYearIncome = row.currentYearIncome || 0;
      const totalAvailableIncome = unremittedIncomeBf + currentYearIncome;
      const totalUsedIncome = incomeReceived + incomeUsedNotReceived;

      if (totalUsedIncome > totalAvailableIncome) {
        if (field === "incomeReceived") processedValue = totalAvailableIncome;
        else updatedRows[rowIndex]["incomeReceived"] = totalAvailableIncome;
        if (field === "incomeUsedNotReceived") processedValue = 0;
        else updatedRows[rowIndex]["incomeUsedNotReceived"] = 0;
      }
    }

    updatedRows[rowIndex][field] = processedValue;

    const calculatedTrackingData = calculateTrackingSchedule(
      updatedRows,
      rowId
    );

    setTrackingScheduleData(calculatedTrackingData);
  };

  // Totals for Current Year table (recompute only when currentYearData changes)
  const currentYearTotals = useMemo(() => {
    return calculateIncomeTotals(currentYearData);
  }, [currentYearData]);

  // Totals for Previous Year table (recompute only when previousYearData changes)
  const previousYearTotals = useMemo(() => {
    return calculateIncomeTotals(previousYearData);
  }, [previousYearData]);

  // Flat totals for backward compatibility (used in table footer)
  const flatTotals = useMemo(() => {
    return trackingScheduleData.reduce(
      (totals, row) => {
        return {
          unremittedIncomeBf:
            totals.unremittedIncomeBf + (row.unremittedIncomeBf || 0),
          currentYearIncome:
            totals.currentYearIncome + (row.currentYearIncome || 0),
          incomeReceived: totals.incomeReceived + (row.incomeReceived || 0),
          incomeUsedNotReceived:
            totals.incomeUsedNotReceived + (row.incomeUsedNotReceived || 0),
          unremittedIncomeCf:
            totals.unremittedIncomeCf + (row.unremittedIncomeCf || 0),
          allowableExpensesBf:
            totals.allowableExpensesBf + (row.allowableExpensesBf || 0),
          currentYearAllowableExpenses:
            totals.currentYearAllowableExpenses +
            (row.currentYearAllowableExpenses || 0),
          shareOfExpensesRemitted:
            totals.shareOfExpensesRemitted + (row.shareOfExpensesRemitted || 0),
          shareOfExpensesNonRemitted:
            totals.shareOfExpensesNonRemitted +
            (row.shareOfExpensesNonRemitted || 0),
          allowableExpensesCf:
            totals.allowableExpensesCf + (row.allowableExpensesCf || 0),
          netIncomeReceived:
            totals.netIncomeReceived + (row.netIncomeReceived || 0),
          foreignTaxSuffered:
            totals.foreignTaxSuffered + (row.foreignTaxSuffered || 0),
          shareOfForeignTaxSuffered:
            totals.shareOfForeignTaxSuffered +
            (row.shareOfForeignTaxSuffered || 0),
        };
      },
      {
        unremittedIncomeBf: 0,
        currentYearIncome: 0,
        incomeReceived: 0,
        incomeUsedNotReceived: 0,
        unremittedIncomeCf: 0,
        allowableExpensesBf: 0,
        currentYearAllowableExpenses: 0,
        shareOfExpensesRemitted: 0,
        shareOfExpensesNonRemitted: 0,
        allowableExpensesCf: 0,
        netIncomeReceived: 0,
        foreignTaxSuffered: 0,
        shareOfForeignTaxSuffered: 0,
      }
    );
  }, [trackingScheduleData]);

  // Subtotals for Tracking Schedule grouped by income type and country
  const trackingSubtotals = useMemo(() => {
    return calculateTotalsByIncomeTypeAndCountry(trackingScheduleData);
  }, [trackingScheduleData]);

  return (
    <Box sx={{ padding: 4 }}>
      <ScheduleHeader
        {...{
          title: schedule.type,
          companyName: company.name,
          companyType: company.companyType,
          uen: company.uen,
          yearOfAssessment: company.yearOfAssessment,
          financialYearStart: company.financialYearStart,
          financialYearEnd: company.financialYearEnd,
          currency: company.currency,
          handleSave,
          deleteSchedule,
          handleExcel: handleOpenExcelUploadDialog,
          canEditSchedule,
        }}
      >
        <PdfGenerator
          schedule={schedule}
          company={company}
          currentYearUnremittedForeignRows={currentYearData}
          previousYearUnremittedForeignRows={previousYearData}
          foreignSourceTrackingRows={trackingScheduleData}
          trackingSubtotals={flatTotals}
          summaryData={trackingSubtotals}
          foreignTaxCreditScheduleRef={foreignTaxCreditScheduleRef}
          notes={notes}
          reviewNotes={reviewNotes}
        />
      </ScheduleHeader>
      <br />

      <ScheduleContainer
        key={schedule.id}
        id={schedule.id}
        schedule={schedule}
        schName={schName}
        taxAdjustment={schedule.taxAdjustment}
        allowedToEditSchName
        allowedToAdjustTax={false}
        updateHeading={(newTitle) => {
          schedule.name = newTitle;
          setSchName(newTitle);
        }}
        onHowItWorksClick={() => setGuideOpen(true)}
        canEditSchedule={canEditSchedule}
      >
        {(fullScreenMode) => (
          <ForeignIncomeTrackerTabs
            canEdit={canEditSchedule}
            currentYearData={currentYearData}
            previousYearData={previousYearData}
            calculatedTrackingData={trackingScheduleData}
            currentYearTotals={currentYearTotals}
            previousYearTotals={previousYearTotals}
            ftcClaims={ftcClaims}
            onFtcClaimChange={(incomeType, country, claimed) => {
              setFtcClaims((prev) => {
                const newClaims = { ...prev };
                if (!newClaims[incomeType]) {
                  newClaims[incomeType] = {};
                }
                newClaims[incomeType][country] = claimed;
                return newClaims;
              });
            }}
            onCurrentYearIncomeChange={handleCurrentYearIncomeChange}
            onPreviousYearIncomeChange={handlePreviousYearIncomeChange}
            onTrackingScheduleChange={handleTrackingScheduleChange}
            onLockRow={handleLockRow}
            onUnlockRow={handleUnlockRow}
            onLockPreviousYearRow={handleLockPreviousYearRow}
            onUnlockPreviousYearRow={handleUnlockPreviousYearRow}
            onDeletePreviousYearRow={handleDeletePreviousYearRow}
            onAddPreviousYearRow={addPreviousYearRow}
            onSortPreviousYearRows={handleSortPreviousYearRows}
            previousYearSortDirection={previousYearSortDirection}
            onSortTrackingRows={handleSortTrackingRows}
            trackingSortDirection={trackingSortDirection}
          />
        )}
      </ScheduleContainer>

      <ForeignIncomeGuideline
        open={guideOpen}
        onClose={() => setGuideOpen(false)}
      />

      <Divider sx={{ marginY: 4 }} />

      <ScheduleNotes
        notes={notes}
        setNotes={setNotes}
        reviewNotes={reviewNotes}
        setReviewNotes={setReviewNotes}
        enableEdit={canEditSchedule}
      />
      <ForeignIncomeTrackerExcelUpload
        open={excelDialogOpen}
        handleCloseDialog={handleCloseExcelUploadDialog}
        onExcelAddRows={onExcelAddRows}
      />
    </Box>
  );
};

export default ForeignIncomeTrackerSchedule;
